import {
  ArrowRight,
  CircleUserRound,
  Menu,
  Search,
  ShoppingBag,
  WifiOff,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";

import {
  navigationFor,
  switchLocalePath,
} from "@/content/routes";
import { legalPendingNotice, t } from "@/content/site-content";
import type { Locale } from "@/domain/schemas";
import { cn } from "@/lib/utils";
import { useDemoStore } from "@/state/demo-store";

import { buttonVariants } from "../ui/button";

export function SiteLayout({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <a
        href="#main-content"
        className="fixed left-3 top-3 z-[100] -translate-y-24 rounded-full bg-primary px-4 py-3 font-semibold text-white focus:translate-y-0"
      >
        {locale === "en" ? "Skip to main content" : "跳至主要內容"}
      </a>
      <DemoRibbon locale={locale} />
      <OfflineBanner locale={locale} />
      <SiteHeader locale={locale} />
      <main id="main-content" tabIndex={-1}>
        {children}
      </main>
      <SiteFooter locale={locale} />
    </div>
  );
}

function DemoRibbon({ locale }: { locale: Locale }) {
  return (
    <div className="bg-ink px-4 py-2 text-center text-xs leading-5 text-white">
      {locale === "en"
        ? "Interactive DEMO · Prices were checked against official pages on 2026-07-24 · No live payment or shipment"
        : "互動 DEMO・價格核對日 2026-07-24・不會產生正式付款、出貨或發票"}
    </div>
  );
}

function OfflineBanner({ locale }: { locale: Locale }) {
  const [offline, setOffline] = useState(false);
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    update();
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  if (!offline) return null;
  return (
    <div
      className="flex items-center justify-center gap-2 bg-warning px-4 py-2 text-sm font-semibold text-warning-ink"
      role="status"
    >
      <WifiOff className="size-4" aria-hidden="true" />
      {locale === "en"
        ? "You are offline. Browsed pages remain available; submissions need a connection."
        : "目前離線。已瀏覽頁面仍可查看；送出資料需要網路連線。"}
    </div>
  );
}

function SiteHeader({ locale }: { locale: Locale }) {
  const [path] = useLocation();
  const { cartCount, user } = useDemoStore();
  const navigation = navigationFor(locale);
  const base = locale === "en" ? "/en" : "/zh-tw";
  const nextLocale: Locale = locale === "en" ? "zh-TW" : "en";
  const localeLabel = locale === "en" ? "中文" : "EN";
  const menuRef = useRef<HTMLDetailsElement>(null);
  const closeMenu = () => menuRef.current?.removeAttribute("open");

  return (
    <header className="sticky top-0 z-40 border-b border-ink/10 bg-canvas/95 backdrop-blur-md">
      <div className="site-container flex min-h-[4.75rem] items-center gap-3">
        <Link
          href={base}
          className="mr-auto inline-flex min-h-11 items-center gap-2 rounded-lg focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/40"
          aria-label={locale === "en" ? "Living Forward home" : "哩皿食器首頁"}
        >
          <img
            src="/assets/official/brand-logo.webp"
            width="88"
            height="48"
            alt=""
            className="h-10 w-auto rounded-sm object-contain"
          />
          <span className="hidden text-sm font-bold tracking-[0.16em] text-primary sm:inline">
            {locale === "en" ? "LIVING FORWARD" : "哩皿食器"}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary">
          {navigation.map((item) => {
            const active = path === item.href || path.startsWith(`${item.href}/`);
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "inline-flex min-h-11 items-center rounded-full px-3 text-sm font-semibold text-ink hover:bg-ink/5 hover:text-primary focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/40",
                  active && "bg-primary/10 text-primary",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center sm:flex">
          <Link
            href={`${base}/search`}
            className={buttonVariants({ variant: "quiet", size: "icon" })}
            aria-label={locale === "en" ? "Search" : "搜尋"}
          >
            <Search className="size-5" aria-hidden="true" />
          </Link>
          <Link
            href={`${base}/account/${user ? "profile" : "login"}`}
            className={buttonVariants({ variant: "quiet", size: "icon" })}
            aria-label={locale === "en" ? "Member account" : "會員中心"}
          >
            <CircleUserRound className="size-5" aria-hidden="true" />
          </Link>
        </div>

        <Link
          href={switchLocalePath(path, nextLocale)}
          className={buttonVariants({ variant: "quiet", size: "sm" })}
          lang={nextLocale === "en" ? "en" : "zh-Hant"}
          hrefLang={nextLocale === "en" ? "en" : "zh-TW"}
        >
          {localeLabel}
        </Link>

        <Link
          href={`${base}/cart`}
          className={cn(buttonVariants({ variant: "secondary", size: "icon" }), "relative")}
          aria-label={
            locale === "en" ? `Cart, ${cartCount} items` : `購物車，共 ${cartCount} 件`
          }
        >
          <ShoppingBag className="size-5" aria-hidden="true" />
          {cartCount > 0 ? (
            <span className="absolute -right-1 -top-1 grid min-h-5 min-w-5 place-items-center rounded-full bg-accent px-1 text-[0.65rem] font-bold text-white">
              {cartCount}
            </span>
          ) : null}
        </Link>

        <details
          ref={menuRef}
          className="xl:hidden"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              closeMenu();
              event.currentTarget.querySelector<HTMLElement>("summary")?.focus();
            }
          }}
        >
          <summary
            className={cn(
              buttonVariants({ variant: "quiet", size: "icon" }),
              "list-none [&::-webkit-details-marker]:hidden",
            )}
            aria-label={locale === "en" ? "Open menu" : "開啟選單"}
          >
              <Menu className="size-5" aria-hidden="true" />
          </summary>
          <div
            className="fixed inset-0 z-40 bg-ink/50"
            aria-hidden="true"
            onClick={closeMenu}
          />
          <section
            className="fixed right-0 top-0 z-50 h-dvh w-[min(90vw,24rem)] overflow-y-auto bg-canvas p-6 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-navigation-title"
          >
            <div className="flex items-start gap-4">
              <div className="mr-auto">
                <h2 id="mobile-navigation-title" className="text-2xl font-bold text-ink">
                  {locale === "en" ? "Explore" : "網站導覽"}
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted">
                  {locale === "en"
                    ? "Shop, learn, or start a business enquiry."
                    : "選購食器、閱讀材質資訊，或開始企業詢價。"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMenu}
                className={buttonVariants({ variant: "quiet", size: "icon" })}
                aria-label={locale === "en" ? "Close menu" : "關閉選單"}
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="mt-8 grid gap-2" aria-label="Mobile primary">
              {navigation.map((item) => (
                  <Link
                    onClick={closeMenu}
                    key={item.href}
                    href={item.href}
                    className="flex min-h-12 items-center justify-between rounded-xl px-3 text-base font-semibold text-ink hover:bg-primary/8 hover:text-primary focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/40"
                  >
                    {item.label}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
              ))}
            </nav>
            <div className="mt-8 border-t border-ink/15 pt-6">
                <Link
                  onClick={closeMenu}
                  href={`${base}/account/${user ? "profile" : "login"}`}
                  className={buttonVariants({ variant: "secondary" })}
                >
                  <CircleUserRound className="size-4" aria-hidden="true" />
                  {locale === "en" ? "Member account" : "會員中心"}
                </Link>
            </div>
          </section>
        </details>
      </div>
    </header>
  );
}

function SiteFooter({ locale }: { locale: Locale }) {
  const base = locale === "en" ? "/en" : "/zh-tw";
  return (
    <footer className="mt-20 border-t border-ink/10 bg-ink text-white">
      <div className="site-container grid gap-10 py-14 md:grid-cols-[1.2fr_1fr_1fr]">
        <div className="max-w-md">
          <p className="text-lg font-bold tracking-[0.12em]">
            {locale === "en" ? "LIVING FORWARD" : "哩皿食器"}
          </p>
          <p className="mt-4 text-sm leading-7 text-white/75">
            {locale === "en"
              ? "A single-site DEMO for consumer shopping, business procurement, OEM/ODM/OBM, and circular partnerships."
              : "整合消費者購物、企業採購、OEM／ODM／OBM 與循環合作的單一官網 DEMO。"}
          </p>
          <p className="mt-4 rounded-xl border border-white/15 bg-white/5 p-3 text-xs leading-6 text-white/75">
            {t(legalPendingNotice, locale)}
          </p>
        </div>
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-white/65">
            {locale === "en" ? "Explore" : "快速入口"}
          </h2>
          <ul className="mt-4 grid gap-2 text-sm">
            {[
              [locale === "en" ? "Shop" : "選購食器", `${base}/products`],
              [locale === "en" ? "Business" : "企業採購", `${base}/business`],
              [locale === "en" ? "Knowledge" : "永續知識", `${base}/knowledge`],
              [locale === "en" ? "Questions" : "常見問題", `${base}/faq`],
            ].map(([label, href]) => (
              <li key={href}>
                <Link className="inline-flex min-h-10 items-center text-white/80 hover:text-white" href={href}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-white/65">
            {locale === "en" ? "Policies" : "服務與政策"}
          </h2>
          <ul className="mt-4 grid gap-2 text-sm">
            {[
              [locale === "en" ? "Shipping" : "配送說明", `${base}/shipping`],
              [locale === "en" ? "Returns" : "退換貨", `${base}/returns-policy`],
              [locale === "en" ? "Privacy" : "隱私權", `${base}/privacy`],
              [locale === "en" ? "Terms" : "服務條款", `${base}/terms`],
            ].map(([label, href]) => (
              <li key={href}>
                <Link className="inline-flex min-h-10 items-center text-white/80 hover:text-white" href={href}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="site-container flex flex-col gap-2 py-5 text-xs text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 {locale === "en" ? "Living Forward DEMO" : "哩皿食器 DEMO"}</p>
          <p>
            {locale === "en"
              ? "Official-source content · Provider integrations disabled"
              : "官方來源內容・正式 provider 未啟用"}
          </p>
        </div>
      </div>
    </footer>
  );
}

export function PageIntro({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <section className="site-container pb-10 pt-12 sm:pt-16">
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <h1 className="mt-3 max-w-4xl text-balance text-[clamp(2.15rem,1.5rem+2.7vw,4.75rem)] font-bold leading-[1.05] tracking-[-0.035em]">
        {title}
      </h1>
      {description ? (
        <p className="mt-5 max-w-3xl text-pretty text-base leading-8 text-muted sm:text-lg">
          {description}
        </p>
      ) : null}
      {children ? <div className="mt-7 flex flex-wrap gap-3">{children}</div> : null}
    </section>
  );
}

export function SourceNote({
  locale,
  sourceUrl,
  checkedAt = "2026-07-24",
}: {
  locale: Locale;
  sourceUrl: string;
  checkedAt?: string;
}) {
  return (
    <aside className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm leading-6 text-muted">
      <p>
        {locale === "en" ? "Official source checked " : "官方來源核對日："}
        <time dateTime={checkedAt}>{checkedAt}</time>
      </p>
      <a
        href={sourceUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-1 inline-flex min-h-10 items-center gap-2 font-semibold text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
      >
        {locale === "en" ? "Open original source" : "查看原始官方頁面"}
        <ArrowRight className="size-4" aria-hidden="true" />
      </a>
    </aside>
  );
}

export function StatePanel({
  icon,
  title,
  description,
  action,
  tone = "neutral",
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  tone?: "neutral" | "error" | "success" | "warning";
}) {
  return (
    <div
      className={cn(
        "rounded-3xl border p-6 text-center sm:p-8",
        tone === "error" && "border-danger/30 bg-danger/5",
        tone === "success" && "border-success/30 bg-success/5",
        tone === "warning" && "border-warning-strong/30 bg-warning/30",
        tone === "neutral" && "border-ink/15 bg-surface",
      )}
      role={tone === "error" ? "alert" : "status"}
    >
      {icon ? <div className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-canvas">{icon}</div> : null}
      <h2 className="text-xl font-bold">{title}</h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-7 text-muted">{description}</p>
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}
